/*
 *  ~ Copyright (c) by INFODATA-GROUP. All rights reserved.
 *  ~ No part of this publication may be reproduced, distributed,
 *  ~ or transmitted in any form or by any means, including photocopying, recording,
 *  ~ or other electronic or mechanical methods, without the prior written permission of the publisher,
 *  ~ except in the case of brief quotations embodied in critical
 *  ~ reviews and certain other noncommercial uses permitted by copyright law.
 *  ~ Author : BS
 *  ~ Date : 3/9/23, 5:24 PM
 */

import { inject, Injectable } from '@angular/core';
import { GmapsTheme } from '../model/gmaps-theme';
import {
  BehaviorSubject,
  delay,
  mergeMap,
  Observable,
  Observer,
  Subject,
  Subscription,
} from 'rxjs';
import {
  InitService,
  RestService,
  SettingsService,
} from '@infodata-group/inf-commons';
import { tap } from 'rxjs/operators';
import { DataEventEnum } from '../enum/data-event.enum';
import { PreferencesSettingsKeyEnum } from '../enum/preferences-settings-key.enum';
import { CompanyFactory } from '../factory/company.factory';
import { Company } from '../model/company';
import { CompanyParams } from '../engine/company-params';
import * as uuid from 'uuid';

@Injectable({
  providedIn: 'root',
})
export class AoSharedDataService {
  protected readonly restService = inject(RestService);
  protected readonly initService = inject(InitService);
  protected readonly settingsService = inject(SettingsService);

  public requestedPage = '';
  /**
   * @Deprecated:: No use of this property since google change theme map API
   */
  public gmapsThemes: { data: GmapsTheme[]; date: Date } = {
    data: [],
    date: null,
  };
  protected emitter$ = new Subject<DataEventEnum>();
  public isLoading = true;
  protected delay = 500;

  public init(): void {
    this.loadSettings$()
      .pipe(
        delay(this.delay),
        mergeMap(() => this.loadCompanySettings$()),
      )
      .subscribe({
        next: () => {
          this.emitter$.next(DataEventEnum.allLoaded);
          this.isLoading = false;
        },
      });
  }

  public clear() {
    this.requestedPage = '';
    this.isLoading = true;
    CompanyParams.clear();
  }

  protected loadSettings$(): Observable<boolean> {
    const emitter$ = new Subject<boolean>();
    if (this.initService.isLoaded) {
      this.emitter$.next(DataEventEnum.settingsLoaded);
      return new BehaviorSubject<boolean>(true).asObservable();
    }
    this.initService.subscribe((event) => {
      if (event.name === 'settings') {
        this.emitter$.next(DataEventEnum.settingsLoaded);
        emitter$.next(true);
      }
    });
    return emitter$.asObservable();
  }

  protected loadCompanySettings$(): Observable<any> {
    return this.restService.get(new CompanyFactory()).pipe(
      tap((company: Company) => {
        if (company.colors.length > 0) {
          CompanyParams.colors = company.colors;
        }
      }),
    );
  }

  public subscribe(observer: Partial<Observer<DataEventEnum>>): Subscription {
    return this.emitter$.subscribe(observer);
  }

  /**
   * @Deprecated:: No use of this method since google change theme map API
   */
  public getCurrentGmapsTheme(): google.maps.MapTypeStyle[] {
    const gmapsThemeId = this.settingsService.getSetting(
      PreferencesSettingsKeyEnum.gmapsTheme,
    );
    if (!gmapsThemeId) {
      return [];
    }
    const gmapsTheme = this.gmapsThemes.data.find((t) => t.id === gmapsThemeId);
    if (!gmapsTheme) {
      return [];
    }
    return gmapsTheme.mapTypeStyle;
  }

  /**
   * @Deprecated:: Use getCurrentGmapsThemeId from SharedMapSettingsService
   */
  public getCurrentGmapsThemeId(): string {
    const data = {
      MAP_001: '4b1d8d4fd94dc567940c9618',
      MAP_002: '4b1d8d4fd94dc56772850cbd',
      MAP_003: '4b1d8d4fd94dc567b4f7434d',
      MAP_004: '4b1d8d4fd94dc567d2545c97',
      MAP_005: '4b1d8d4fd94dc567b1baa223',
      MAP_006: '4b1d8d4fd94dc567a1d77b63',
      MAP_007: '4b1d8d4fd94dc567faec2adc',
      MAP_008: 'MAP_008',
    };
    const gmapsThemeId = this.settingsService.getSetting(
      PreferencesSettingsKeyEnum.gmapsTheme,
    );
    if (!gmapsThemeId) {
      return '';
    }
    if (Object.hasOwn(data, gmapsThemeId)) {
      return data[gmapsThemeId];
    }
    return uuid.v4();
  }
}
